import {ErrorOutlineIcon} from '@sanity/icons'
import {Box, Card, Flex, Text, Tooltip} from '@sanity/ui'
import debounce from 'lodash-es/debounce.js'
import {type RefObject, useCallback, useMemo} from 'react'
import {type TFunction, useTranslation} from 'sanity'

import {paramsExtensions} from '../codemirror/extensions'
import {VisionCodeMirror, type VisionCodeMirrorHandle} from '../codemirror/VisionCodeMirror'
import {visionLocaleNamespace} from '../i18n'
import {tryParseParams} from '../util/tryParseParams'
import {type Params} from './VisionGui'
import {InputBackgroundContainerLeft, StyledLabel} from './VisionGui.styled'

const defaultValue = `{\n  \n}`

export interface ParamsEditorProps {
  value: string
  onChange: (changeEvt: string) => void
  paramsError: string | undefined
  hasValidParams: boolean
  editorRef: RefObject<VisionCodeMirrorHandle | null>
}

export function ParamsEditor(props: ParamsEditorProps) {
  const {onChange, paramsError, hasValidParams, editorRef} = props
  const {t} = useTranslation(visionLocaleNamespace)

  const handleChangeRaw = useCallback(
    (newValue: string) => {
      onChange(newValue)
    },
    [onChange],
  )

  const handleChange = useMemo(() => debounce(handleChangeRaw, 333), [handleChangeRaw])
  return (
    <Card flex={1} tone={hasValidParams ? 'default' : 'critical'} data-testid="params-editor">
      <InputBackgroundContainerLeft>
        <Flex>
          <StyledLabel muted>{t('params.label')}</StyledLabel>
          {paramsError && (
            <Tooltip animate placement="top" portal content={<Text size={1}>{paramsError}</Text>}>
              <Box padding={1} marginX={2}>
                <Text>
                  <ErrorOutlineIcon />
                </Text>
              </Box>
            </Tooltip>
          )}
        </Flex>
      </InputBackgroundContainerLeft>
      <VisionCodeMirror
        ref={editorRef}
        initialValue={props.value || defaultValue}
        onChange={handleChange}
        extensions={paramsExtensions}
      />
    </Card>
  )
}

export function parseParams(value: string, t: TFunction<typeof visionLocaleNamespace>): Params {
  const parsedParams = tryParseParams(value, t)
  const params = parsedParams instanceof Error ? {} : parsedParams
  const validationError = parsedParams instanceof Error ? parsedParams.message : undefined
  const isValid = !validationError

  return {
    parsed: params,
    raw: value,
    valid: isValid,
    error: validationError,
  }
}
